import { FC, useEffect, useRef } from 'react'
import { InvoicingAddressFields, InvoicingAddressFormFields, useInvoicingAddressForm } from 'components/forms/InvoicingAddress'
import { Stack, Typography } from '@mui/material'

import { GRAY_1000 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckCard } from 'components/common/MUICards'
import { QueryStatus } from 'components/common/QueryStatus'
import { UseMutationResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

/**
 * Props for the NewInvoicingAddressCard component
 */
interface Props {
  /**
   * Indicates whether this card is currently selected
   */
  checked: boolean

  /**
   * Mutation used to create a new invoicing address
   * Used for loading & error state feedback
   */
  mutation: UseMutationResult<any, any, any>

  /**
   * Callback triggered when the card is selected
   */
  onCheck: () => void

  /**
   * Callback triggered when a new invoicing address is confirmed
   */
  onConfirmAddress: (addressPayload: InvoicingAddressFields) => void
}

/**
 * Renders a selectable card that allows the user to create
 * and save a new invoicing address.
 *
 * @remarks
 * - When expanded, the card displays:
 *   - The invoicing address form
 *   - Error states for the create mutation
 *   - A submit button to create & select the address
 * - Form validity is handled via `useInvoicingAddressForm`.
 */
export const NewInvoicingAddressCard: FC<Props> = ({
  checked,
  mutation,
  onCheck,
  onConfirmAddress,
}) => {

  /** Translation helper for all texts inside this component */
  const { t } = useTranslation(['invoicing_address_pf'])

  /** Access form state & helpers from the InvoicingAddressForm context */
  const { isValid, getFormValues, reset, fields } = useInvoicingAddressForm()

  /** Tracks previous checked state to reset form when deselected */
  const prevCheckedState = useRef(false)

  useEffect(() => {
    if (!checked && prevCheckedState.current === true) { reset() }
    prevCheckedState.current = checked
  }, [checked, reset])

  return (
    <MUICheckCard
      isRadio
      expandableContent={(
        <Stack gap={2}>

          {/* Invoicing address form fields */}
          <InvoicingAddressFormFields />

          {/* Displays error state from mutation if it occurs */}
          <QueryStatus
            query={mutation}
            showStates={['error']}
          />

          {/* W2.6 (BX-F10): "save first" nudge. When the user has started
              filling in the form (addressName non-empty) but hasn't yet
              clicked "Create & use address" (mutation is idle), a helper
              line explains that saving is required before the order can
              proceed. Without this, selectBillingStepValid blocks Place
              Order silently with no inline explanation. Hidden once the
              mutation is in-flight or has succeeded. */}
          {checked && mutation.isIdle && !!fields.addressName.value &&
            <Typography variant="text-sm" color="text.secondary">
              {t('save_first_nudge')}
            </Typography>
          }

          {/* Create & use address button */}
          <MUIButton
            onClick={() => {
              if (!isValid) return
              onConfirmAddress(getFormValues())
            }}
            disabled={!isValid}
            isLoading={mutation.isPending}
          >
            {t('create_and_use_button')}
          </MUIButton>

        </Stack>
      )}
      checked={checked}
      onCheck={() => onCheck()}
    >
      {/* Card title */}
      <Typography variant="text-md" fontWeight={500} color={GRAY_1000}>
        {t('new_address_card_title')}
      </Typography>

    </MUICheckCard>
  )
}
